import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable, BehaviorSubject, asyncScheduler } from 'rxjs';
import { finalize, observeOn, distinctUntilChanged } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private activeRequests = 0;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable().pipe(
    distinctUntilChanged(),
    observeOn(asyncScheduler)
  );

  constructor(private http: HttpClient) {}

  private setLoading(loading: boolean): void {
    if (loading) {
      this.activeRequests++;
    } else {
      this.activeRequests--;
    }
    
    this.loadingSubject.next(this.activeRequests > 0);
  }

  /**
   * Get JWT token from localStorage
   */
  private getJwtToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  /**
   * Store JWT token in localStorage
   */
  setJwtToken(token: string): void {
    localStorage.setItem('auth_token', token);
  }

  /**
   * Remove JWT token from localStorage
   */
  clearJwtToken(): void {
    localStorage.removeItem('auth_token');
  }

  /**
   * Get CSRF token from meta tag or cookie
   * Laravel stores CSRF token in meta tag with name 'csrf-token'
   */
  private getCsrfToken(): string | null {
    const meta = document.querySelector('meta[name="csrf-token"]');
    if (meta) {
      return meta.getAttribute('content');
    }
    
    // Fallback: try to get from cookie
    const name = 'XSRF-TOKEN';
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith(name + '='))
      ?.split('=')[1];
    
    return cookieValue || null;
  }

  /**
   * Build HTTP headers with JWT token, CSRF token, and proper content type
   */

  private getHeaders(isFormData: boolean = false): HttpHeaders {
    let headers = new HttpHeaders();

    // Only set Content-Type for JSON requests, not for FormData
    if (!isFormData) {
      headers = headers.set('Content-Type', 'application/json');
    }
    headers = headers.set('Accept', 'application/json');

    // Add JWT token if available
    const jwtToken = this.getJwtToken();
    if (jwtToken) {
      headers = headers.set('Authorization', `Bearer ${jwtToken}`);
    }

    // Add CSRF token if available
    const csrfToken = this.getCsrfToken();
    if (csrfToken) {
      headers = headers.set('X-CSRF-TOKEN', csrfToken);
    }

    return headers;
  }

  get(uri: string): Observable<any> {
    this.setLoading(true);
    return this.http.get(`${environment.apiAddress}/${uri}`, {
      headers: this.getHeaders(),
      withCredentials: true,
    })
      .pipe(finalize(() => this.setLoading(false)));
  }

  post(uri: string, data: any): Observable<any> {
    this.setLoading(true);
    return this.http.post(`${environment.apiAddress}/${uri}`, data, {
      headers: this.getHeaders(),
      withCredentials: true,
    })
      .pipe(finalize(() => this.setLoading(false)));
  }

  post_auto(uri: string, data: any): Observable<any> {
    this.setLoading(true);
    // Detect if data is FormData for file uploads
    const isFormData = data instanceof FormData;

    const httpOptions: any = {
      withCredentials: true,
    };

    // Only add headers if data is not FormData
    // For FormData, we let the browser handle Content-Type with proper boundary
    if (!isFormData) {
      httpOptions.headers = this.getHeaders(false);
    } else {
      // For FormData, only add auth and CSRF headers, omit Content-Type
      const jwtToken = this.getJwtToken();
      let headers = new HttpHeaders();

      if (jwtToken) {
        headers = headers.set('Authorization', `Bearer ${jwtToken}`);
      }

      const csrfToken = this.getCsrfToken();
      if (csrfToken) {
        headers = headers.set('X-CSRF-TOKEN', csrfToken);
      }

      headers = headers.set('Accept', 'application/json');
      httpOptions.headers = headers;
    }

    return this.http.post(`${environment.apiAddress}/${uri}`, data, httpOptions)
      .pipe(finalize(() => this.setLoading(false)));
  }


  put(uri: string, data: any): Observable<any> {
    this.setLoading(true);
    return this.http.put(`${environment.apiAddress}/${uri}`, data, {
      headers: this.getHeaders(),
      withCredentials: true,
    })
      .pipe(finalize(() => this.setLoading(false)));
  }

  delete(uri: string): Observable<any> {
    this.setLoading(true);
    return this.http.delete(`${environment.apiAddress}/${uri}`, {
      headers: this.getHeaders(),
      withCredentials: true,
    })
      .pipe(finalize(() => this.setLoading(false)));
  }

  getAppConfigs(): Observable<any> {
    return this.http.get(`${environment.apiAddress}/app-configs`, {
      headers: this.getHeaders(),
      withCredentials: true,
    });
  }
}

